import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { Field, FormPage, FormSection, QtyInput, QueryStatus, ScaledInput, useToast } from "@cx/ui";
import type { ProductDetail, ProductStructureView, ProductionOrderView } from "@cx/contracts";
import { formatPct, formatQty, toPct4 } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ProductPicker } from "../products/ProductPicker";
import { LotFields } from "../stock/LotFields";
import {
  emptyProductionForm,
  productionFormFromView,
  productionFormProblem,
  productionPayload,
  requiredQ4,
  rowsFromStructure,
  type ProductionForm,
  type StructureRow,
} from "./labels";
import type { ProductionFormOptions } from "./ProductionOrdersListPage";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/** Inclusão (/suprimentos/ordens-de-producao/novo) e edição (…/$id/editar) da ordem de produção. */
export function ProductionOrderFormPage(): ReactElement {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const { companyId, api } = useSession();
  const options = useQuery({
    queryKey: ["production-orders", companyId, "form-options"],
    queryFn: () => api<ProductionFormOptions>("/production-orders/form-options"),
  });
  const order = useQuery({
    queryKey: ["production-orders", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<ProductionOrderView>(`/production-orders/${id}`),
  });
  if (!options.data || (id && !order.data))
    return (
      <QueryStatus loading={options.isLoading || order.isLoading} error={options.error ?? order.error} />
    );
  if (order.data?.lockedReason)
    return (
      <div className="alert alert-warning" role="alert">
        {order.data.lockedReason}
      </div>
    );
  return <ProductionFormBody key={id ?? "new"} options={options.data} order={order.data ?? null} />;
}

function ProductionFormBody({
  options,
  order,
}: {
  options: ProductionFormOptions;
  order: ProductionOrderView | null;
}) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState<ProductionForm>(() =>
    order ? productionFormFromView(order) : emptyProductionForm(),
  );
  const [dirty, setDirty] = useState(false);
  const [tried, setTried] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingProduct, setLoadingProduct] = useState(false);
  // Uma chave por tela de inclusão: clique duplo ou repetição da rede não criam duas ordens.
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = <K extends keyof ProductionForm>(k: K, v: ProductionForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setItem = (i: number, patch: Partial<StructureRow>) =>
    set(
      "items",
      f.items.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    );

  /** Ao escolher o produto, a lista de insumos vem da estrutura dele (editável nesta ordem). */
  const pickProduct = async (id: string) => {
    setLoadingProduct(true);
    try {
      const [detail, structure] = await Promise.all([
        api<ProductDetail>(`/products/${id}`),
        api<ProductStructureView>(`/products/${id}/structure`),
      ]);
      setF((x) => ({
        ...x,
        product: { id, name: detail.name, unit: detail.unit, batchControl: !!detail.batchControl },
        items: rowsFromStructure(structure.items),
      }));
      setDirty(true);
    } catch (e) {
      toast("danger", errorMessage(e as Error));
    } finally {
      setLoadingProduct(false);
    }
  };

  const problem = productionFormProblem(f);
  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(productionPayload(f));
      if (order) {
        await api(`/production-orders/${order.id}`, { method: "PUT", body });
        return order.id;
      }
      const r = await api<{ id: string }>("/production-orders", {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idemKey },
      });
      return r.id;
    },
    onSuccess: (savedId) => {
      toast("success", "Ordem de produção salva");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["production-orders", companyId] });
      navigate({ to: "/suprimentos/ordens-de-producao/$id", params: { id: savedId } });
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });

  const title = order ? `Ordem de produção nº ${order.number}` : "Nova ordem de produção";
  const back = () =>
    order
      ? navigate({ to: "/suprimentos/ordens-de-producao/$id", params: { id: order.id } })
      : navigate({ to: "/suprimentos/ordens-de-producao" });
  const warehouseSelect = (k: "sourceWarehouseId" | "targetWarehouseId") => (
    <select className="form-select" value={f[k]} onChange={(e) => set(k, e.target.value)}>
      <option value="">Padrão da empresa</option>
      {options.warehouses.map((w) => (
        <option key={w.id} value={w.id}>
          {w.name}
        </option>
      ))}
    </select>
  );

  return (
    <FormPage
      title={title}
      breadcrumb={["Suprimentos", "Ordens de produção", order ? String(order.number) : "Nova"]}
      onSave={() => {
        setTried(true);
        if (!problem) save.mutate();
      }}
      onCancel={back}
      saving={save.isPending}
      dirty={dirty}
    >
      <FormSection title="Produto e quantidade">
        <div className="row">
          <div className="col-md-8">
            {f.product ? (
              <div className="mb-3">
                <span className="form-label d-block">Produto a produzir</span>
                <div className="d-flex align-items-center gap-2">
                  <strong data-testid="op-product">{f.product.name}</strong>
                  {!order && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={() => setF((x) => ({ ...x, product: null, items: [] }))}
                    >
                      Trocar produto
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <ProductPicker
                label="Produto a produzir"
                inputId="op-product-search"
                target="como produto a produzir"
                exclude={[]}
                onPick={(p) => void pickProduct(p.id)}
              />
            )}
            {loadingProduct && <p className="text-muted small">Carregando a estrutura…</p>}
            {errors.productId && <div className="text-danger small">{errors.productId}</div>}
          </div>
          <Field label="Quantidade a produzir" required className="col-md-4" error={errors.quantityQ4}>
            <QtyInput value={f.quantityQ4} onChange={(q) => set("quantityQ4", q)} />
          </Field>
          <Field label="Depósito dos insumos" className="col-md-6" error={errors.sourceWarehouseId}>
            {warehouseSelect("sourceWarehouseId")}
          </Field>
          <Field label="Depósito do produto acabado" className="col-md-6" error={errors.targetWarehouseId}>
            {warehouseSelect("targetWarehouseId")}
          </Field>
          <Field label="Responsável" className="col-md-4" error={errors.responsible}>
            <input
              value={f.responsible}
              maxLength={100}
              onChange={(e) => set("responsible", e.target.value)}
            />
          </Field>
          <Field label="Previsão de início" className="col-md-4" error={errors.plannedStart}>
            <input type="date" value={f.plannedStart} onChange={(e) => set("plannedStart", e.target.value)} />
          </Field>
          <Field label="Previsão de término" className="col-md-4" error={errors.plannedEnd}>
            <input type="date" value={f.plannedEnd} onChange={(e) => set("plannedEnd", e.target.value)} />
          </Field>
        </div>
        {f.product?.batchControl && (
          <>
            <p className="text-muted small mb-1">
              O produto controla lotes: informe o lote do que será produzido (pode ser informado em cada
              apontamento).
            </p>
            <LotFields value={f.lot} onChange={(l) => set("lot", l)} />
          </>
        )}
      </FormSection>

      <FormSection title="Insumos">
        {f.product && f.items.length === 0 && !loadingProduct && (
          <p className="text-muted">
            Este produto não tem estrutura. Cadastre os insumos na aba Estrutura do produto ou adicione aqui.
          </p>
        )}
        {f.items.length > 0 && (
          <div
            className="table-responsive mb-3"
            tabIndex={0}
            role="region"
            aria-label="Insumos da ordem de produção (rolagem)"
          >
            <table className="table align-middle" aria-label="Insumos da ordem de produção">
              <thead>
                <tr>
                  <th scope="col">Insumo</th>
                  <th scope="col">Un</th>
                  <th scope="col" className="text-end">
                    Por unidade
                  </th>
                  <th scope="col" className="text-end">
                    Perda (%)
                  </th>
                  <th scope="col" className="text-end">
                    Necessário
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {f.items.map((r, i) => {
                  const need = requiredQ4(r.qtyQ4, r.lossPct4, f.quantityQ4);
                  return (
                    <tr key={r.componentId}>
                      <td>
                        {r.name}
                        {r.sku && <span className="text-muted small ms-2">{r.sku}</span>}
                      </td>
                      <td>{r.unit}</td>
                      <td style={{ minWidth: 110 }}>
                        <QtyInput
                          aria-label={`Quantidade por unidade de ${r.name}`}
                          className="form-control form-control-sm text-end"
                          value={r.qtyQ4}
                          onChange={(v) => setItem(i, { qtyQ4: v })}
                        />
                      </td>
                      <td style={{ minWidth: 90 }}>
                        <ScaledInput
                          aria-label={`Perda de ${r.name}`}
                          className="form-control form-control-sm text-end"
                          value={r.lossPct4}
                          parse={toPct4}
                          format={pctNumber}
                          onChange={(v) => setItem(i, { lossPct4: v })}
                        />
                      </td>
                      <td className="text-end qty">{need === null ? "—" : formatQty(need)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Remover insumo ${r.name}`}
                          onClick={() =>
                            set(
                              "items",
                              f.items.filter((_, j) => j !== i),
                            )
                          }
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {f.product && (
          <ProductPicker
            label="Adicionar insumo"
            inputId="op-component-search"
            target="aos insumos"
            exclude={[...f.items.map((r) => r.componentId), f.product.id]}
            onPick={(p) =>
              set("items", [
                ...f.items,
                { componentId: p.id, name: p.name, sku: p.sku, unit: p.unit, qtyQ4: 10_000, lossPct4: 0 },
              ])
            }
          />
        )}
      </FormSection>

      <FormSection title="Observações">
        <Field label="Observações" className="col-12" error={errors.notes}>
          <textarea
            rows={3}
            maxLength={2000}
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </FormSection>
      {tried && problem && (
        <div className="alert alert-danger py-2" role="alert">
          {problem}
        </div>
      )}
    </FormPage>
  );
}
